import {ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {AccreditationBody, AttachedQuestion, AttachedQuestionQuestionType, Order, PriceFactor, Product, StandardName} from "../../../store/state/order.state";
import {BaseSubFormComponent} from "../../../umbrella-common/components/base-sub-form.component";
import {PriceFactorsFormService} from "./price-factors-form.service";
import {FormGroup} from "@angular/forms";
import {Observable} from "rxjs";
import {TranslationsHelperService} from "../../translations/translations-helper.service";

@Component({
    selector: 'price-factors',
    templateUrl: './price-factors.template.html',
    styleUrls: ['./price-factors.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PriceFactorsComponent extends BaseSubFormComponent implements OnInit, OnChanges, OnDestroy {

	readonly TEXT: AttachedQuestionQuestionType = AttachedQuestionQuestionType.TEXT;

	@Input() priceFactors: PriceFactor[];
	@Input() order: Order;
	@Input() product: Product;
	form: FormGroup;

	constructor(private formService: PriceFactorsFormService, private translationsHelper: TranslationsHelperService) {
		super(formService);
	}

	ngOnInit(): void {
		if (this.priceFactors.length > 0) {
			this.form = this.formService.buildSubForm(this.product.standard.name, this.totalSubForms(this.order), this.priceFactors, this.product);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes['product'] && changes['product'].currentValue != null && changes['product'].previousValue != null) {
			if (changes['product'].currentValue.priceFactors.some(pf => pf.attachedQuestions != null)) {
				this.formService.addAttachedQuestionTypeIfNeeded(this.product, this.priceFactors);
			}
		}
	}

	getFormKey(): string {
		return this.product.standard.name;
	}

	trackByPriceFactorFn(index: number, item: any) {
		return index;
	}

	anyPriceFactorInvalid(): boolean {
		return this.formService.anyPriceFactorInvalid(this.product);
	}

	getAttachedQuestionErrors(priceFactorType: string, attachedQuestionType: string): Observable<string> {
		return this.formService.getErrorsString(this.getFormKey(), this.formService.getAttachedQuestionFormControlNamePrefix(priceFactorType, attachedQuestionType));
	}

	isAttachedQuestionInvalid(priceFactorType: string, attachedQuestionType: string): boolean {
		return this.isInvalid(this.formService.getAttachedQuestionFormControlNamePrefix(priceFactorType, attachedQuestionType));
	}

	shouldDisableRadio(controlNamePrefix: string): boolean {
		return this.formService.getFormControl(this.product.standard.name, controlNamePrefix + this.getFormKey()).disabled;
	}

	attachedQuestionVisible(attachedQuestion: AttachedQuestion, priceFactorType: string): boolean {
		const priceFactor = this.product.priceFactors.find(pf => pf.type == priceFactorType);
		if (priceFactor) {
			return attachedQuestion.parentQuestionAnswerValue == priceFactor.positiveAnswer;
		}
		return false;
	}

	translationExists(priceFactorType: string, attachedQuestionType: string): boolean {
		return this.translationsHelper.translationExists(this.product.standard.name, this.translationSubject(priceFactorType, attachedQuestionType), false);
	}

	translationSubject(priceFactorType: string, attachedQuestionType: string): string {
		return `priceFactor[${priceFactorType}][${attachedQuestionType}]`;
	}

	getTranslationText(priceFactorType: string, attachedQuestionType: string): string {
		return this.translationsHelper.getTranslation(this.product.standard.name, this.translationSubject(priceFactorType, attachedQuestionType), false);
	}

	override totalSubForms(order: Order): number {
		let iso27kAnabCount = order.products.filter(p => [StandardName.ISO_27001, StandardName.ISO_27701].includes(p.standard.name)).filter(p => p.accreditationBody === AccreditationBody.ANAB).length;
		let iso27701RvaCount = order.products.filter(p => p.standard.name == StandardName.ISO_27701).filter(p => p.accreditationBody === AccreditationBody.RVA).length;
		let productsWithoutPriceFactors = iso27kAnabCount + iso27701RvaCount;
		return order.products.length - productsWithoutPriceFactors;
	}

	protected iso27701HasItsOwnSubForm(): boolean {
		return false;
	}
}
